export type ShopifyCartOperation =
	| 'create'
	| 'add'
	| 'update-lines'
	| 'remove'
	| 'discount-codes'
	| 'buyer-identity'
	| 'attributes'
	| 'read';

/** Intentionally contains no request bodies, credentials, cookie values, or IP addresses. */
export interface ShopifyCartRequestDiagnostic {
	operation: ShopifyCartOperation;
	transport: 'custom' | 'sdk';
	upstreamHost: string | null;
	storefrontId: string | null;
	originHost: string | null;
	storefrontOriginHost: string | null;
	referrerHost: string | null;
	cookieNames: string[];
	buyerIpPresent: boolean;
	publicTokenConfigured: boolean;
	privateTokenConfigured: boolean;
	status: number | null;
	apiClientId: string | null;
	requestId: string | null;
}

const COOKIE_NAMES = new Set([
	'_shopify_essential',
	'_shopify_analytics',
	'_shopify_marketing',
	'_shopify_y',
	'_shopify_s',
	'_tracking_consent',
]);

function hostname(value: string | null): string | null {
	if (!value || value.length > 4096) return null;
	try {
		const url = new URL(value);
		if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
		const host = url.hostname.toLowerCase();
		return /^[a-z0-9.-]{1,128}$/.test(host) ? host : null;
	} catch {
		return null;
	}
}

function numericId(value: string | null): string | null {
	return value && /^\d{1,20}$/.test(value) ? value : null;
}

export function buildShopifyCartRequestDiagnostic(
	operation: ShopifyCartOperation,
	url: string,
	requestHeaders: Headers,
	response?: Response,
	transport: ShopifyCartRequestDiagnostic['transport'] = 'custom',
): ShopifyCartRequestDiagnostic {
	const cookieNames = new Set<string>();
	for (const cookie of (requestHeaders.get('cookie') ?? '').split(';')) {
		const separator = cookie.indexOf('=');
		if (separator < 1) continue;
		const name = cookie.slice(0, separator).trim();
		if (COOKIE_NAMES.has(name)) cookieNames.add(name);
	}
	const requestId = response?.headers.get('x-request-id') ?? null;
	return {
		operation,
		transport,
		upstreamHost: hostname(url),
		storefrontId: numericId(requestHeaders.get('Shopify-Storefront-Id')),
		originHost: hostname(requestHeaders.get('origin')),
		storefrontOriginHost: hostname(requestHeaders.get('Sec-Shopify-Storefront-Origin')),
		referrerHost: hostname(requestHeaders.get('referer')),
		cookieNames: [...cookieNames].sort(),
		buyerIpPresent: Boolean(requestHeaders.get('Shopify-Storefront-Buyer-IP')?.trim()),
		publicTokenConfigured: Boolean(requestHeaders.get('X-Shopify-Storefront-Access-Token')?.trim()),
		privateTokenConfigured: Boolean(requestHeaders.get('Shopify-Storefront-Private-Token')?.trim()),
		status: response?.status ?? null,
		apiClientId: numericId(response?.headers.get('x-stats-apiclientid') ?? null),
		requestId: requestId && /^[a-f0-9]{8}-[a-f0-9-]{27,71}$/i.test(requestId) ? requestId : null,
	};
}
